/**
 * components/beams/Beam.tsx — MUTA parametric beam (PLAN §2.18, T14).
 *
 * PRESENTATION component: receives a `BeamSpec` ALREADY resolved by
 * `model/` (center, quaternion, dims) and renders it as a box.
 * DOES NOT calculate positions/rotations: `spec.center` and `spec.quaternion`
 * I'm already in the world (mm, three.js Y-up).
 *
 * FIBER CONVENTION (FIX C): the fibers of the beam are the local X axis.
 * `boxGeometry args = [l, h, b]` (l = length of fibers on X, h = height
 * vertical, b = width/thickness in the plane of the joint face).
 * The quaternion (THREE.Quaternion already resolved by the model) transforms the
 * local X (fibres) in the world (SPEC G8).
 *
 * - If `spec.notch` is present and `showDebug` is active, draw a box
 * orange wireframe (COLORS.notchDebug) that marks the cut area
 * at the end of the beam along the fibers (visual helper, it is NOT the cut
 * CSG — v1 does not run boolean).
 * - Edges for a technical reading of the geometry.
 *
 * Boundary rules (SPEC §6): `components/` is MUTE — no computation.
 * The quaternion ALREADY comes as THREE.Quaternion from the model (chain
 * kinematics — no tuple conversion in render).*/

import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { CSG } from "three-csg-ts";
import { COLORS } from "../../config/colors";
import type { BeamSpec } from "../../model/types";
import { assignWoodMaterialGroups, createWoodSurface } from "./woodMaterial";

// ─── Props (PLAN §2.18) ───────────────────────────────────────

export interface BeamProps {
  /** Beam specification ALREADY resolved (center, quaternion, dims, notch).*/
  spec: BeamSpec;
  /** Beam color (from config/colors.ts).*/
  color: string;
  /** Show debug helpers (edge ​​+ box notch).*/
  showDebug?: boolean;
  /** Materials opacity (0..1) — overlay from componentsState (T15).*/
  opacity?: number;
  /** Group visibility (overlay from componentsState).*/
  visible?: boolean;
}

//─── Component ─────────────────────── ────────────────────────

/**
 * Render the parametric beam from the resolved BeamSpec.
 * The group uses position/rotation from the final quaternion; the materials
 * are standard (roughness/metalness wood/steel) with edge lines.*/
export function Beam({ spec, color, showDebug = false, opacity = 1, visible = true }: BeamProps) {
  //Position and orientation from model (NEVER recalculated here).
  const position = spec.center;
  const quaternion = spec.quaternion; // already a THREE.Quaternion (kinematic chain)

  const hasDimensions = spec.dims.b > 0 && spec.dims.h > 0 && spec.dims.l > 0;
  const geometry = useMemo(
    () => (hasDimensions ? beamGeometryWithNotch(spec) : new THREE.BufferGeometry()),
    [hasDimensions, spec.dims.b, spec.dims.h, spec.dims.l, spec.notch, spec.skewAlphaDeg, spec.skewBetaDeg],
  );
  // Geometria creata a mano (non dichiarata nel JSX): R3F non la libera, quindi
  // ogni cambio di dims/notch/skew lasciava un buffer GPU orfano.
  useEffect(() => () => geometry.dispose(), [geometry]);
  const woodSurface = useMemo(
    () =>
      createWoodSurface({
        color,
        opacity,
        lengthMm: spec.dims.l,
        sectionMm: Math.max(spec.dims.b, spec.dims.h),
      }),
    [color, opacity, spec.dims.b, spec.dims.h, spec.dims.l],
  );

  useEffect(
    () => () => {
      woodSurface.materials.forEach((material) => material.dispose());
      woodSurface.textures.forEach((texture) => texture.dispose());
    },
    [woodSurface],
  );

  //B/H arrive exclusively from the form: until they are filled in, they do not
  //we render the beam, nor do we use a default geometric measurement.
  if (!hasDimensions) return null;

  //DEBUG box notch: marks the cut area at the end of the beam
  //along the fiber axis (local position +l/2 − thickness/2, fibers =
  const notch = spec.notch;
  const notchBox =
    showDebug && notch ? (
      <mesh position={notchLocalPosition(spec)}>
        <boxGeometry args={notchLocalSize(spec)} />
        <meshBasicMaterial color={COLORS.notchDebug} wireframe transparent opacity={0} depthWrite={false} />
      </mesh>
    ) : null;

  return (
    <group position={position} quaternion={quaternion} visible={visible}>
      {/* Corpo principale: box L×H×B centrato (FIX C: fibre = local X → args [l, h, b]). */}
      <mesh geometry={geometry} material={woodSurface.materials} castShadow receiveShadow />

      {/* Spigoli tecnici (edge wireframe). */}
      <Edges geometry={geometry} />

      {/* Zona notch (debug). */}
      {notchBox}
    </group>
  );
}

//─── Sub-components (internal, not exported) ────────────────

/**
 * Edges — contour lines of the beam box (visual helper).
 * Use THREE.EdgesGeometry to show geometry edges.
 * Args [l, h, b] consistent with the convention fibers = local X (FIX C).*/
function notchLocalPosition(spec: BeamSpec): [number, number, number] {
  const notch = spec.notch;
  if (!notch) return [0, 0, 0];
  if (notch.target === "joist-beam") {
    //SOLO per il box di debug (invisibile, opacity 0 in Beam()): un singolo
    //centro che approssima la faccia sheared. Il cutter CSG reale NON usa
    //piu' l'offset Y/Z qui sotto (bug corretto 01-10-2026, vedi
    //`buildNotchCutterGeometry`) — un singolo punto di riferimento lascia un
    //cuneo non tagliato lontano dal centro, perche' lo shear della trave e'
    //continuo lungo x mentre questo box resta piatto.
    const alphaRad = THREE.MathUtils.degToRad(spec.skewAlphaDeg);
    const betaRad = THREE.MathUtils.degToRad(spec.skewBetaDeg);
    const projectedLength = spec.dims.l * Math.cos(alphaRad);
    const connectorFaceOffsetY = (spec.dims.l * Math.sin(alphaRad)) / 2;
    const connectorFaceOffsetZ = (spec.dims.l * Math.cos(alphaRad) * Math.tan(betaRad)) / 2;
    return [-projectedLength / 2 + notch.depth / 2, connectorFaceOffsetY, connectorFaceOffsetZ];
  }
  // An header-column target can be selected while the form is transitioning
  // orientation. The cutter follows the actual beam frame, never the label.
  if (notch.target === "header-beam" || spec.axis === "z") {
    // Ry(-90°) maps local +Z to world -X: the junction face is local -Z.
    return [0, spec.dims.h / 2 - notch.height / 2, -spec.dims.b / 2 + notch.depth / 2];
  }
  // Column frame: local X is vertical and local Y points toward world -X.
  return [spec.dims.l / 2 - notch.height / 2, -spec.dims.h / 2 + notch.depth / 2, 0];
}

function notchLocalSize(spec: BeamSpec): [number, number, number] {
  const notch = spec.notch;
  if (!notch) return [0, 0, 0];
  if (notch.target === "joist-beam") return [notch.depth, notch.height, notch.width];
  if (notch.target === "header-beam" || spec.axis === "z") return [notch.width, notch.height, notch.depth];
  return [notch.height, notch.depth, notch.width];
}

function beamGeometryWithNotch(spec: BeamSpec): THREE.BufferGeometry {
  const base = beamBaseGeometry(spec);
  if (!spec.notch) {
    assignWoodMaterialGroups(base);
    return base;
  }
  const cutterGeometry = buildNotchCutterGeometry(spec);
  const cutter = new THREE.Mesh(cutterGeometry);
  cutter.updateMatrix();
  const beam = new THREE.Mesh(base);
  beam.updateMatrix();
  const cut = CSG.subtract(beam, cutter).geometry;
  base.dispose();
  cutter.geometry.dispose();
  cut.computeVertexNormals();
  assignWoodMaterialGroups(cut);
  return cut;
}

/**
 * Geometria del cutter CSG per il notch (01-10-2026, bug segnalato
 * dall'utente — "il taglio viene deformato in modo che tagli tutto
 * verticalmente", con screenshot: un cuneo di legno non tagliato all'angolo
 * superiore quando alpha e' attivo).
 *
 * PRIMA: il cutter era un box PIATTO (non shearato) posizionato con
 * `notchLocalPosition`, che calcola un singolo centro Y/Z che approssima SOLO
 * il punto a x = centro-profondita' del taglio (`connectorFaceOffsetY/Z` =
 * esattamente lo shear della trave valutato in quel punto). La trave pero' e'
 * shearata CONTINUAMENTE lungo x (`beamBaseGeometry`: `y' = y − x·tan(alpha)`):
 * allontanandosi da quel singolo punto, lungo tutta la profondita' del
 * cutter, la superficie reale della trave continua a spostarsi mentre il
 * cutter (piatto) resta fermo — risultato: un cuneo non tagliato a un'estremita'
 * della profondita' e un eccesso tagliato all'altra.
 *
 * ORA: il cutter e' costruito nello STESSO sistema di riferimento locale
 * della trave (centrato su x = 0 come la trave stessa prima di shearare,
 * NESSUN pre-offset Y/Z), poi ESATTAMENTE la stessa deformazione DI ALPHA di
 * `beamBaseGeometry` viene applicata alla sua geometria (non alla posizione
 * della mesh): ogni vertice del cutter subisce lo stesso `y' = y − x·tan(alpha)`
 * della trave alla sua stessa x, quindi resta allineato su TUTTA la
 * profondita' del taglio, non solo al centro.
 *
 * SOLO ALPHA SI DEFORMA (richiesta utente 01-10-2026: "bisogna deformarlo
 * lungo alfa e non beta"): a differenza di `beamBaseGeometry` (che sheara il
 * CORPO della trave su entrambi gli assi), il cutter NON riceve la
 * deformazione CONTINUA di beta lungo la profondita' — ma la posizione Z
 * resta comunque traslata di `connectorFaceOffsetZ` (bug corretto 01-10-2026,
 * "il notch si e' spostato": avevo tolto anche la traslazione Z insieme alla
 * deformazione, spostando il taglio quando beta e' attivo — qui serve solo
 * UN pre-offset statico, non uno shear che varia con x, quindi resta una
 * traslazione sola, mai dentro la matrice di shear sotto).*/
function buildNotchCutterGeometry(spec: BeamSpec): THREE.BufferGeometry {
  const notch = spec.notch;
  const geometry = new THREE.BoxGeometry(...notchLocalSize(spec));
  if (!notch) return geometry;

  if (notch.target !== "joist-beam") {
    geometry.translate(...notchLocalPosition(spec));
    return geometry;
  }

  //X: centro-profondita' del taglio, PRIMA dello shear di alpha (lo shear
  //sotto lo applica da solo, niente pre-offset Y). Z: `connectorFaceOffsetZ`
  //(da beta) come semplice TRASLAZIONE statica, non deformazione — vedi
  //doc-comment sopra.
  const [x0, , z0] = notchLocalPosition(spec);
  geometry.translate(x0, 0, z0);

  if (spec.skewAlphaDeg !== 0) {
    const alphaRad = THREE.MathUtils.degToRad(spec.skewAlphaDeg);
    geometry.applyMatrix4(
      new THREE.Matrix4().set(
        1, 0, 0, 0,
        -Math.tan(alphaRad), 1, 0, 0,
        0, 0, 1, 0,
        0, 0, 0, 1,
      ),
    );
  }
  return geometry;
}

function beamBaseGeometry(spec: BeamSpec): THREE.BufferGeometry {
  if (spec.role !== "joist" || (spec.skewAlphaDeg === 0 && spec.skewBetaDeg === 0)) {
    return new THREE.BoxGeometry(spec.dims.l, spec.dims.h, spec.dims.b);
  }

  const alphaRad = THREE.MathUtils.degToRad(spec.skewAlphaDeg);
  const betaRad = THREE.MathUtils.degToRad(spec.skewBetaDeg);
  const projectedLength = spec.dims.l * Math.cos(alphaRad);
  const geometry = new THREE.BoxGeometry(projectedLength, spec.dims.h, spec.dims.b);
  //x remains the horizontal projection; y varies linearly along x (alpha,
  //X-Y plane) and z varies linearly along x (beta, X-Z plane — same family,
  //independent axis). The distance between the two end faces remains L and
  //alpha/beta > 0 drop towards -Y / -Z respectively.
  geometry.applyMatrix4(
    new THREE.Matrix4().set(
      1, 0, 0, 0,
      -Math.tan(alphaRad), 1, 0, 0,
      -Math.tan(betaRad), 0, 1, 0,
      0, 0, 0, 1,
    ),
  );
  geometry.computeVertexNormals();
  return geometry;
}

function Edges({ geometry }: { geometry: THREE.BufferGeometry }) {
  // Ricalcola l'edge geometry solo quando i dims cambiano.
  const edges = useMemo(() => new THREE.EdgesGeometry(geometry), [geometry]);
  useEffect(() => () => edges.dispose(), [edges]);

  return (
    <lineSegments geometry={edges}>
      <lineBasicMaterial color={COLORS.edge} transparent opacity={0.5} />
    </lineSegments>
  );
}
